// ============================================
// ConverseTech MVP - Shared Constants
// ============================================

import type { TrainingMode, SessionState, DETPhaseConfig } from './types';

// === Training Modes ===
export const TRAINING_MODES: readonly TrainingMode[] = [
  'conversation',
  'technical_explanation',
  'mock_interview',
  'duolingo_test',
] as const;

export const TRAINING_MODE_LABELS: Record<TrainingMode, string> = {
  conversation: 'Conversation',
  technical_explanation: 'Technical Explanation',
  mock_interview: 'Mock Interview',
  duolingo_test: 'Duolingo DET Simulation',
};

export const TRAINING_MODE_DESCRIPTIONS: Record<TrainingMode, string> = {
  conversation: 'Practice fluency with open-ended conversation',
  technical_explanation: 'Structure practice: context, problem, solution, trade-offs',
  mock_interview: 'Interview prep with pressure and follow-up questions',
  duolingo_test: 'DET-style tasks with strict time limits',
};

// === Session States ===
export const SESSION_STATES: readonly SessionState[] = [
  'INIT',
  'WARMUP',
  'DRILL',
  'PRESSURE',
  'FEEDBACK',
  'COMPLETE',
] as const;

export const SESSION_STATE_LABELS: Record<SessionState, string> = {
  INIT: 'Initializing',
  WARMUP: 'Warm Up',
  DRILL: 'Main Practice',
  PRESSURE: 'Under Pressure',
  FEEDBACK: 'Feedback',
  COMPLETE: 'Complete',
};

// Phase durations in milliseconds
export const PHASE_DURATIONS: Partial<Record<SessionState, number>> = {
  WARMUP: 5 * 60 * 1000,   // 5 minutes
  DRILL: 10 * 60 * 1000,   // 10 minutes
  PRESSURE: 5 * 60 * 1000, // 5 minutes
  FEEDBACK: 3 * 60 * 1000, // 3 minutes
};

// === Audio Configuration ===
export const AUDIO_CONFIG = {
  INPUT_SAMPLE_RATE: 16000,   // 16kHz for Gemini input
  OUTPUT_SAMPLE_RATE: 24000,  // 24kHz for Gemini output
  CHANNELS: 1,                // Mono
  CHUNK_DURATION_MS: 250,     // Audio chunk duration
  MAX_MESSAGE_SIZE: 1024 * 1024, // 1MB Cloudflare limit
} as const;

// === Metrics Configuration ===
export const METRICS_CONFIG = {
  // Filler words to detect
  FILLERS: ['um', 'uh', 'like', 'you know', 'basically', 'actually', 'so', 'well'] as const,

  // Hedging phrases to detect
  HEDGING: ['maybe', 'perhaps', 'kind of', 'sort of', 'I think', 'I guess', 'probably'] as const,

  // Structure markers for technical explanation
  STRUCTURE_MARKERS: {
    intro: ['context', 'background', 'overview', 'about'],
    problem: ['problem', 'challenge', 'issue', 'difficulty'],
    solution: ['solution', 'approach', 'implementation', 'how'],
    tradeoffs: ['tradeoff', 'trade-off', 'limitation', 'downside', 'however', 'but'],
  } as const,

  // Scoring weights
  STRUCTURE_WEIGHTS: {
    intro: 10,
    problem: 25,
    solution: 35,
    tradeoffs: 30,
  } as const,

  // DET score weights (must sum to 1.0)
  DET_WEIGHTS: {
    fluency: 0.30,
    structure: 0.30,
    confidence: 0.15,
    pressure: 0.10,
    vocabulary: 0.15,
  } as const,

  // Penalties
  FILLER_PENALTY: 2,
  HEDGING_PENALTY: 3,

  // DET score range
  DET_MIN: 10,
  DET_MAX: 160,

  // Target WPM for fluency normalization
  TARGET_WPM: 150,

  // Pause threshold in milliseconds
  PAUSE_THRESHOLD_MS: 500,
} as const;

// === API Endpoints ===
export const API_ENDPOINTS = {
  HEALTH: '/api/health',
  USERS_ME: '/api/users/me',
  SESSIONS: '/api/sessions',
  SESSIONS_START: '/api/sessions/start',
  DASHBOARD_OVERVIEW: '/api/dashboard/overview',
} as const;

// === Vocabulary Configuration ===
export const VOCABULARY_CONFIG = {
  // Scoring weights (must sum to 1.0)
  WEIGHTS: {
    diversity: 0.40,    // Type-token ratio
    advanced: 0.35,     // Complex word usage
    academic: 0.25,     // Academic vocabulary
  } as const,

  // Complex prefixes indicating advanced vocabulary
  ADVANCED_PREFIXES: [
    'anti', 'auto', 'bi', 'circum', 'co', 'counter', 'de', 'dis',
    'ex', 'fore', 'hyper', 'inter', 'intra', 'macro', 'micro',
    'mid', 'mis', 'mono', 'multi', 'neo', 'non', 'omni', 'out',
    'over', 'poly', 'post', 'pre', 'pro', 'pseudo', 'quasi',
    're', 'semi', 'sub', 'super', 'trans', 'tri', 'ultra', 'un', 'under',
  ] as const,

  // Complex suffixes indicating advanced vocabulary
  ADVANCED_SUFFIXES: [
    'ability', 'ation', 'ative', 'ence', 'ency', 'ible', 'ical',
    'ification', 'ism', 'ist', 'ity', 'ization', 'ize', 'ment',
    'ness', 'ology', 'ous', 'ship', 'tion', 'ure',
  ] as const,

  // Academic Word List (AWL) - common words in academic/technical contexts
  // Subset of Coxhead's Academic Word List
  ACADEMIC_WORDS: [
    'analyze', 'approach', 'area', 'assess', 'assume', 'authority',
    'available', 'benefit', 'concept', 'consistent', 'constitute',
    'context', 'contract', 'create', 'data', 'define', 'derive',
    'distribute', 'economy', 'environment', 'establish', 'estimate',
    'evaluate', 'evident', 'export', 'factor', 'finance', 'formula',
    'function', 'identify', 'income', 'indicate', 'individual',
    'interpret', 'involve', 'issue', 'labor', 'legal', 'legislate',
    'major', 'method', 'occur', 'percent', 'period', 'policy',
    'principle', 'proceed', 'process', 'require', 'research',
    'respond', 'role', 'section', 'sector', 'significant', 'similar',
    'source', 'specific', 'structure', 'theory', 'vary',
    // Technical/software terms
    'algorithm', 'architecture', 'asynchronous', 'authentication',
    'bandwidth', 'buffer', 'cache', 'callback', 'component',
    'concurrent', 'configuration', 'database', 'dependency',
    'deployment', 'distributed', 'encryption', 'endpoint',
    'framework', 'implementation', 'infrastructure', 'integration',
    'interface', 'latency', 'middleware', 'optimization', 'parameter',
    'performance', 'persistence', 'protocol', 'query', 'redundancy',
    'repository', 'scalability', 'schema', 'synchronous', 'throughput',
    'transaction', 'validation', 'virtualization',
  ] as const,

  // Minimum word length to consider for advanced vocabulary
  MIN_ADVANCED_WORD_LENGTH: 6,

  // Minimum words needed for meaningful vocabulary analysis
  MIN_WORDS_FOR_ANALYSIS: 20,
} as const;

// === Gemini Configuration ===
export const GEMINI_CONFIG = {
  MODEL: 'models/gemini-2.5-flash-native-audio-preview-12-2025',
  VOICE_NAME: 'Aoede',
  WEBSOCKET_URL: 'wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContent',
} as const;

// === Time Limits ===
export const TIME_LIMITS = {
  MOCK_INTERVIEW_ANSWER: 90 * 1000, // 90 seconds
  DET_SHORT_TASK: 30 * 1000,        // 30 seconds
  DET_LONG_TASK: 90 * 1000,         // 90 seconds
  TECHNICAL_EXPLANATION: 2 * 60 * 1000, // 2 minutes
  // DET Phase-specific time limits
  DET_READ_ALOUD: 30 * 1000,        // 30 seconds
  DET_SHORT_ANSWER: 30 * 1000,      // 30 seconds
  DET_EXTENDED_SPEAKING: 90 * 1000, // 90 seconds
} as const;

// === DET Phase Configuration ===
// Maps DET phases to existing SessionState values
export const DET_PHASES: readonly DETPhaseConfig[] = [
  {
    phase: 'read_aloud',
    sessionState: 'WARMUP',
    duration: TIME_LIMITS.DET_READ_ALOUD,
    label: 'Read Aloud',
    index: 1,
  },
  {
    phase: 'short_answer',
    sessionState: 'DRILL',
    duration: TIME_LIMITS.DET_SHORT_ANSWER,
    label: 'Short Answer',
    index: 2,
  },
  {
    phase: 'extended_speaking',
    sessionState: 'PRESSURE',
    duration: TIME_LIMITS.DET_EXTENDED_SPEAKING,
    label: 'Extended Speaking',
    index: 3,
  },
] as const;

// === UI Configuration ===
export const UI_CONFIG = {
  MAX_TRANSCRIPT_ENTRIES: 100,
  RECONNECT_ATTEMPTS: 3,
  RECONNECT_DELAY_MS: 1000,
  TOAST_DURATION_MS: 5000,
} as const;
